import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

const API_KEY = "AIzaSyC1RP-8w-sZV7NrfiZ6_kNBugeJTycj-h4";
const BLOG_ID = "6772292124788323362";
const POSTS_PER_PAGE = 6;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [nextPageToken, setNextPageToken] = useState(null);
  const [categories, setCategories] = useState(new Set());
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async (token = "") => {
    try {
      setLoading(true);
      let url = `https://www.googleapis.com/blogger/v3/blogs/${BLOG_ID}/posts?key=${API_KEY}&maxResults=${POSTS_PER_PAGE}`;
      if (token) url += `&pageToken=${token}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.items) {
        const newPosts = data.items.map(post => {
          const imgMatch = post.content.match(/<img.*?src=["'](.*?)["']/);
          const imageUrl = imgMatch ? imgMatch[1] : "https://via.placeholder.com/400";
          const publishedDate = new Date(post.published);
          const formattedDate = `${publishedDate.toLocaleDateString()} ${publishedDate.toLocaleTimeString()}`;

          return {
            id: post.id,
            title: post.title || "Untitled Post",
            image: imageUrl,
            category: post.labels?.[0] || "Uncategorized",
            content: post.content,
            publishedDate: formattedDate,
          };
        });

        setPosts(prev => {
          const allPosts = [...prev, ...newPosts];
          const uniquePosts = Array.from(new Map(allPosts.map(post => [post.id, post])).values());
          return uniquePosts;
        });

        setCategories(new Set([...categories, ...newPosts.map(post => post.category)]));
        setNextPageToken(data.nextPageToken || null);
      } else {
        setPosts([]);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to load blog posts.");
    } finally {
      setLoading(false);
    }
  };

  const loadMorePosts = () => {
    if (nextPageToken) {
      fetchPosts(nextPageToken);
    }
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const filteredPosts = selectedCategories.length
    ? posts.filter(post => selectedCategories.includes(post.category))
    : posts.filter(post => post.title.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <>
      <div style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        padding: "20px",
        marginTop: "50px",
        width: "100vw",
        boxSizing: "border-box"
      }}>
        
        {/* Main Content */}
        <div style={{ flex: 3, marginRight: isMobile ? "0" : "30px" }}>
          <h1 style={{ textAlign: "center", fontSize: isMobile ? "1.8rem" : "2.5rem" }}>Blog</h1>
          <input
            type="text"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedCategories([]);
            }}
            style={{
              padding: "10px",
              marginBottom: "20px",
              width: "50%",
              borderRadius: "5px",
              border: "1px solid #ccc",
              fontSize: "1rem",
            }}
          />
          {loading && <p>Loading...</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}
          
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
            gap: "20px",
            width: "100%"
          }}>
            {filteredPosts.map((post) => (
              <motion.div
                key={post.id}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                style={{
                  background: "white",
                  padding: "0px",
                  borderRadius: "10px",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                  width: "100%"
                }}
              >
                <img 
                  src={post.image} 
                  alt={post.title} 
                  style={{ width: "100%", height: "250px", objectFit: "cover", borderRadius: "10px" }} 
                />
                <h2 style={{ padding: "15px", fontSize: "1.2rem" }}>{post.title}</h2>
                <p style={{ paddingLeft: "15px", fontSize: "0.9rem", color: "#555" }}>
                  Published on: {post.publishedDate}
                </p>

                <div style={{ padding: "0 15px 15px" }}>
                  <button
                    onClick={() => setSelectedPost(post)}
                    style={readMoreStyle}
                  >
                    Read More
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          
          {nextPageToken && (
            <button
              onClick={loadMorePosts}
              style={{
                marginTop: "20px",
                padding: "12px",
                background: "blue",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                width: isMobile ? "100%" : "auto",
                fontSize: "1rem",
              }}
            >
              Load More
            </button>
          )}
        </div>

        {/* Sidebar */}
        <div style={{
          flex: 1,
          padding: "20px",
          background: "#f8f9fa",
          borderRadius: "10px",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          marginTop: isMobile ? "40px" : "0",
        }}>
          <h2>Categories</h2>
          <div>
            <button onClick={() => setSelectedCategories([])} style={categoryButtonStyle(selectedCategories.length === 0)}>
              Show All
            </button>
            {Array.from(categories).map((category, index) => (
              <div key={index} style={{ marginBottom: "10px" }}>
                <input
                  type="checkbox"
                  id={category}
                  checked={selectedCategories.includes(category)}
                  onChange={() => toggleCategory(category)}
                  style={{ marginRight: "10px" }}
                />
                <label htmlFor={category} style={{ cursor: "pointer", fontSize: "16px" }}>
                  {category}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Overlay Modal */}
      {selectedPost && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h2>{selectedPost.title}</h2>
            <p style={{ fontSize: "0.9rem", color: "#555" }}>
              Published on: {selectedPost.publishedDate}
            </p>
            <img src={selectedPost.image} alt={selectedPost.title} style={{ width: "100%", borderRadius: "10px", marginBottom: "15px" }} />
            <div
              dangerouslySetInnerHTML={{ __html: selectedPost.content }}
              style={{ maxHeight: "400px", overflowY: "auto", marginBottom: "20px" }}
            />
            <button onClick={() => setSelectedPost(null)} style={closeButtonStyle}>
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Styles
const categoryButtonStyle = (isActive) => ({
  marginBottom: "10px",
  padding: "10px",
  background: isActive ? "#5c0e14" : "transparent",
  color: isActive ? "white" : "black",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  width: "100%",
  textAlign: "center",
  fontSize: "16px"
});

const readMoreStyle = {
  background: "none",
  color: "blue",
  border: "none",
  padding: "0",
  textDecoration: "underline",
  cursor: "pointer",
  fontSize: "1rem",
  marginTop: "10px"
};

const overlayStyle = {
  position: "fixed",
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000
};

const modalStyle = {
  background: "white",
  padding: "30px",
  borderRadius: "10px",
  width: "90%",
  maxWidth: "800px",
  maxHeight: "90vh",
  overflowY: "auto"
};

const closeButtonStyle = {
  padding: "10px 20px",
  background: "#5c0e14",
  color: "white",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer"
};

export default BlogPage;
